import { ChangeEvent, RefObject, useEffect, useRef } from "react"

/**
 * Committing a number field while it is being STEPPED, not only on blur / Enter.
 *
 * Stepping — the field's spin arrows, ↑ / ↓, the mouse wheel — moves the value one step at a
 * time and should show on the canvas at once. Typing should not: "120" would be committed
 * as 1, 12 and 120, three undo steps and three timeline rebuilds. Typing still commits on
 * blur / Enter.
 */

/**
 * Whether a change came from stepping. Typing, pasting and deleting fire an InputEvent with
 * an `inputType` ("insertText", "deleteContentBackward"…); the browser's own stepping (spin
 * arrows, ↑ / ↓) fires a plain input event without one.
 */
export const isStepChange = (event: ChangeEvent<HTMLInputElement>): boolean =>
    !(event.nativeEvent as InputEvent).inputType

/** `current` moved by one `step` in `direction`, rounded to the step's decimals and clamped. */
export const stepNumber = (
    current: number,
    direction: 1 | -1,
    step: number = 1,
    min?: number,
    max?: number
): number => {
    const decimals = (String(step).split(".")[1] ?? "").length
    const factor = 10 ** decimals
    let next = Math.round(((Number(current) || 0) + direction * step) * factor) / factor
    if (min !== undefined) next = Math.max(min, next)
    if (max !== undefined) next = Math.min(max, next)
    return next
}

/**
 * Step the field with the mouse wheel while it has focus — the page does not scroll then.
 * A field without focus leaves the wheel alone, so scrolling the panel over it still works.
 * Native listener: React's wheel listener is passive and could not stop the scroll.
 */
export const useWheelStep = (
    inputRef: RefObject<HTMLInputElement>,
    onStep: (direction: 1 | -1) => void,
    enabled: boolean = true
) => {
    const onStepRef = useRef(onStep)
    onStepRef.current = onStep

    useEffect(() => {
        const input = inputRef.current
        if (!input || !enabled) return
        const handleWheel = (event: WheelEvent) => {
            if (document.activeElement !== input || event.deltaY === 0) return
            event.preventDefault()
            onStepRef.current(event.deltaY < 0 ? 1 : -1)
        }
        input.addEventListener("wheel", handleWheel, { passive: false })
        return () => input.removeEventListener("wheel", handleWheel)
    }, [inputRef, enabled])
}
